import React from 'react';
import styles from './Select.module.css';

/**
 * Select Component
 *
 * A styled select dropdown with label and error support.
 *
 * @param {Object} props
 * @param {string} props.label - Select label
 * @param {string} props.error - Error message
 * @param {Array} props.options - Array of {value, label} options
 * @param {'sm' | 'md' | 'lg'} props.size - Select size
 * @param {boolean} props.fullWidth - Full width select
 * @param {string} props.className - Additional CSS classes
 *
 * @example
 * <Select
 *   label="Asset Type"
 *   options={[
 *     { value: 'stock', label: 'Stock' },
 *     { value: 'crypto', label: 'Cryptocurrency' }
 *   ]}
 *   value={assetType}
 *   onChange={(e) => setAssetType(e.target.value)}
 * />
 */
const Select = React.forwardRef(({
  label,
  error,
  options = [],
  size = 'md',
  fullWidth = false,
  className = '',
  id,
  children,
  ...props
}, ref) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  const wrapperClassNames = [
    styles.wrapper,
    fullWidth && styles.fullWidth,
    className
  ].filter(Boolean).join(' ');

  const selectClassNames = [
    styles.select,
    styles[size],
    error && styles.error
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClassNames}>
      {label && (
        <label htmlFor={selectId} className={styles.label}>
          {label}
        </label>
      )}

      <div className={styles.selectContainer}>
        <select
          ref={ref}
          id={selectId}
          className={selectClassNames}
          {...props}
        >
          {children || options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <span className={styles.icon} aria-hidden="true">
          ▼
        </span>
      </div>

      {error && (
        <span className={styles.errorText}>
          {error}
        </span>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
